import {
  BasicPermission,
  createPermission,
} from '@backstage/plugin-permission-common';

/**
 * Every KSDP permission name starts with this prefix. The KSDP permission
 * policy only judges permissions carrying it and leaves every other
 * Backstage permission (catalog, scaffolder, ...) untouched.
 */
export const KSDP_PERMISSION_PREFIX = 'ksdp.';

/** Application Security plugin: app list/detail and every module. */
export const ksdpAppsecReadPermission = createPermission({
  name: 'ksdp.appsec.read',
  attributes: { action: 'read' },
});

/** Application Security write actions, except the Monitoring module. */
export const ksdpAppsecEditPermission = createPermission({
  name: 'ksdp.appsec.edit',
  attributes: { action: 'update' },
});

/** Update an application's Monitoring module. */
export const ksdpMonitoringEditPermission = createPermission({
  name: 'ksdp.monitoring.edit',
  attributes: { action: 'update' },
});

/** Compliance Management plugin: Standards Library and assignments. */
export const ksdpComplianceMgmtReadPermission = createPermission({
  name: 'ksdp.compliance-mgmt.read',
  attributes: { action: 'read' },
});

/** Create/edit/transition/archive standards, groups, requirements, links; assign apps. */
export const ksdpComplianceMgmtEditPermission = createPermission({
  name: 'ksdp.compliance-mgmt.edit',
  attributes: { action: 'update' },
});

/** Exceptions plugin: case list/detail and settings. */
export const ksdpExceptionsReadPermission = createPermission({
  name: 'ksdp.exceptions.read',
  attributes: { action: 'read' },
});

/** Reply to and resolve exception cases; change the reopen window. */
export const ksdpExceptionsManagePermission = createPermission({
  name: 'ksdp.exceptions.manage',
  attributes: { action: 'update' },
});

/** Risk Library: catalogue, AI-generated risks, find applications by risk. */
export const ksdpRiskReadPermission = createPermission({
  name: 'ksdp.risk.read',
  attributes: { action: 'read' },
});

/** Trigger the Confluence re-import of the risk catalogue. */
export const ksdpRiskImportPermission = createPermission({
  name: 'ksdp.risk.import',
  attributes: { action: 'create' },
});

/** Checklist Approval plugin: pending checklists and checklist detail. */
export const ksdpChecklistReadPermission = createPermission({
  name: 'ksdp.checklist.read',
  attributes: { action: 'read' },
});

/** Edit and approve pending checklists. */
export const ksdpChecklistApprovePermission = createPermission({
  name: 'ksdp.checklist.approve',
  attributes: { action: 'update' },
});

/** Local Security (GRR) dashboard: country scores, regulations and sites. */
export const ksdpGrrReadPermission = createPermission({
  name: 'ksdp.grr.read',
  attributes: { action: 'read' },
});

/** All KSDP permissions, for registration and exhaustive tests. */
export const ksdpPermissions: readonly BasicPermission[] = [
  ksdpAppsecReadPermission,
  ksdpAppsecEditPermission,
  ksdpMonitoringEditPermission,
  ksdpComplianceMgmtReadPermission,
  ksdpComplianceMgmtEditPermission,
  ksdpExceptionsReadPermission,
  ksdpExceptionsManagePermission,
  ksdpRiskReadPermission,
  ksdpRiskImportPermission,
  ksdpChecklistReadPermission,
  ksdpChecklistApprovePermission,
  ksdpGrrReadPermission,
];

/** True when the permission belongs to KSDP (and so is judged by the KSDP policy). */
export function isKsdpPermissionName(name: string): boolean {
  return name.startsWith(KSDP_PERMISSION_PREFIX);
}
